import { FeatureCart } from '@mdlbeasts/nofomo.ui.web';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { isAfter } from 'date-fns';
import { type ReactNode, useMemo } from 'react';

import { useTranslatedText } from 'hooks/useTranslatedText';
import { useTrackViewItem } from 'services/datalayer/ecomm/useTrackViewItem';
import { TicketStatusFragment } from 'types/api/dato-operations.types';
import { TicketFragment } from 'types/api/tickets.types';

import Icon from 'components/@icons/Icon';
import { useTicketInfo } from 'components/@tickets/hooks';
import TicketPrice from 'components/@tickets/TicketPrice';

interface Props {
  ticket: TicketFragment;
  children: ReactNode;
  status?: TicketStatusFragment;
}

const TicketItemAccordion = ({ ticket, status, children }: Props) => {
  const localize = useTranslatedText();
  const { originalPrice, discountedPrice, isSoldOut } = useTicketInfo(
    ticket.id,
  );
  const handleViewItem = useTrackViewItem();

  const description = localize(ticket.description);

  const handleTrackViewChange = (expanded: boolean) => {
    if (expanded) handleViewItem(ticket);
  };

  const isStatusActive = useMemo(() => {
    if (!status) {
      return false;
    }

    if (status.statusType === 'schedule' && status?.schedule) {
      //Check if the date has passed
      if (isAfter(new Date(), new Date(status?.schedule))) {
        return false;
      }
    }

    return true;
  }, [status]);

  return (
    <Accordion onChange={(_e, expanded) => handleTrackViewChange(expanded)}>
      <AccordionSummary
        expandIcon={<Icon name="ChevronDown" />}
        aria-controls={`panel${ticket.id}-content`}
        id={`panel${ticket.id}-header`}
        disabled={!description}
      >
        <Stack
          sx={{
            flexGrow: 1,
            gap: 2,
            ml: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexGrow: 1,
              gap: 2,
              ml: 1,
            }}
          >
            <Stack
              sx={{
                gap: 0.5,
              }}
            >
              <Typography
                variant="small"
                component="h3"
                sx={{
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                }}
              >
                {localize(ticket.name)}
              </Typography>

              <TicketPrice
                originalPrice={originalPrice}
                discountedPrice={discountedPrice}
                color="text.secondary"
              />
            </Stack>

            {!isStatusActive && !isSoldOut && <>{children}</>}
          </Box>
          <FeatureCart.TicketItem.Status
            color={status?.color?.hex}
            content={status?.content}
            type={
              status?.statusType as FeatureCart.TicketItem.StatusProps['type']
            }
            schedule={status?.schedule}
            soldOut={isSoldOut}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {!!ticket.perks?.length && (
          <Stack
            sx={{
              gap: 2,
              mb: 2,
            }}
          >
            {ticket.perks.map((perk) => (
              <Box
                key={perk.id}
                sx={{
                  display: 'flex',
                  gap: 1,
                }}
              >
                <Icon name="Check" fontSize={16} color="global01.400" />
                <Typography
                  variant="small"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {localize(perk.name)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        <Typography
          variant="small"
          sx={{
            color: 'text.secondary',
          }}
        >
          {description}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

export default TicketItemAccordion;
